import { useState, useMemo } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, Building2, User } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useCart, formatTL } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import SEO from "@/components/SEO";

export default function OrderForm() {
  const { items, clear, count } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const isDealer = user && user.role === "dealer";
  const dealerDiscount = isDealer ? (user.discount_percent || 0) : 0;

  const [form, setForm] = useState({
    customer_type: "individual",
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    company: user?.company || "",
    tax_id: "",
    tax_office: "",
    address: "",
    city: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const subtotal = useMemo(() => {
    return items.reduce((s, it) => {
      if (isDealer && it.price > 0 && dealerDiscount > 0) {
        return s + it.price * (1 - dealerDiscount / 100) * it.quantity;
      }
      return s + it.price * it.quantity;
    }, 0);
  }, [items, isDealer, dealerDiscount]);

  if (count === 0 && !done) return <Navigate to="/sepet" replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        items: items.map((it) => ({ product_id: it.product_id, quantity: it.quantity })),
      };
      const { data } = await api.post("/orders", payload);
      setDone(data);
      clear();
      toast.success("Siparişiniz alındı!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <section className="pt-32 pb-24">
        <div className="wb-container max-w-xl">
          <div className="wb-card p-12 text-center">
            <div className="w-16 h-16 mx-auto bg-[#d4af37]/10 border border-[#d4af37] flex items-center justify-center">
              <Check size={28} className="text-[#d4af37]" />
            </div>
            <h1 className="wb-h3 mt-6">Siparişiniz Alındı</h1>
            <div className="text-sm text-[#a0aabf] mt-3">
              Sipariş No: <span className="text-[#d4af37] font-mono">#{done.id?.slice(0, 8)}</span>
            </div>
            <div className="font-serif text-2xl mt-4">{formatTL(done.total)}</div>
            <p className="text-sm text-[#a0aabf] mt-5">
              En kısa sürede sizinle iletişime geçeceğiz. Onay e-postası gönderildi.
            </p>
            <div className="mt-8 flex gap-3 justify-center">
              <Link to="/urunler" className="wb-btn-secondary">Alışverişe Devam</Link>
              <button onClick={() => nav("/")} className="wb-btn-primary">Anasayfa</button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <SEO title="Sipariş Formu" />
      <section className="pt-28 pb-12 border-b border-white/10">
        <div className="wb-container">
          <Link to="/sepet" className="text-sm text-[#a0aabf] hover:text-[#d4af37] inline-flex items-center gap-2">
            <ArrowLeft size={14} /> Sepete dön
          </Link>
          <div className="wb-overline mt-4">Sipariş</div>
          <h1 className="wb-h1 mt-3">Sipariş Formu</h1>
        </div>
      </section>

      <section className="wb-section">
        <div className="wb-container grid grid-cols-1 lg:grid-cols-3 gap-8">
          <form onSubmit={submit} className="lg:col-span-2 wb-card p-6 md:p-8 space-y-5" data-testid="order-form">
            <div>
              <div className="wb-overline mb-3">Müşteri Tipi</div>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, customer_type: "individual" })}
                  className={`p-4 border text-left transition-all ${form.customer_type === "individual" ? "border-[#d4af37] bg-[#d4af37]/5" : "border-white/15 hover:border-white/30"}`}
                  data-testid="order-type-individual"
                >
                  <User size={20} className="text-[#d4af37]" />
                  <div className="font-serif text-base mt-2">Bireysel</div>
                  <div className="text-xs text-[#a0aabf] mt-0.5">Şahıs adına fatura</div>
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, customer_type: "corporate" })}
                  className={`p-4 border text-left transition-all ${form.customer_type === "corporate" ? "border-[#d4af37] bg-[#d4af37]/5" : "border-white/15 hover:border-white/30"}`}
                  data-testid="order-type-corporate"
                >
                  <Building2 size={20} className="text-[#d4af37]" />
                  <div className="font-serif text-base mt-2">Kurumsal</div>
                  <div className="text-xs text-[#a0aabf] mt-0.5">Firma adına fatura</div>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input className="wb-input" placeholder="Ad Soyad *" required value={form.name} onChange={set("name")} data-testid="order-name" />
              <input className="wb-input" type="email" placeholder="E-posta *" required value={form.email} onChange={set("email")} data-testid="order-email" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input className="wb-input" placeholder="Telefon *" required value={form.phone} onChange={set("phone")} data-testid="order-phone" />
              <input className="wb-input" placeholder="Şehir *" required value={form.city} onChange={set("city")} data-testid="order-city" />
            </div>

            {form.customer_type === "corporate" && (
              <div className="space-y-4 pt-2 border-t border-white/10">
                <div className="wb-overline mt-4">Fatura Bilgileri (Kurumsal)</div>
                <input className="wb-input" placeholder="Firma Adı *" required value={form.company} onChange={set("company")} data-testid="order-company" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input className="wb-input" placeholder="Vergi No / TC *" required value={form.tax_id} onChange={set("tax_id")} data-testid="order-tax-id" />
                  <input className="wb-input" placeholder="Vergi Dairesi *" required value={form.tax_office} onChange={set("tax_office")} data-testid="order-tax-office" />
                </div>
              </div>
            )}

            <textarea className="wb-input min-h-[100px] resize-none" placeholder="Adres *" required value={form.address} onChange={set("address")} data-testid="order-address" />
            <textarea className="wb-input min-h-[80px] resize-none" placeholder="Sipariş notu (opsiyonel)" value={form.notes} onChange={set("notes")} data-testid="order-notes" />

            <div className="bg-[#d4af37]/5 border border-[#d4af37]/20 p-4 text-xs text-[#a0aabf] leading-relaxed">
              <strong className="text-[#d4af37]">Bilgilendirme:</strong> Fiyatlara KDV %20 dahildir.
              {isDealer
                ? <> Bayi siparişlerinde <strong className="text-[#fdfbf7]">kargo alıcıya aittir</strong>. Sipariş, ekibimiz tarafından onaylandıktan sonra hazırlanacaktır.</>
                : <> Sipariş onayınız ve kargo bilgileri ekibimiz tarafından sizinle paylaşılacaktır.</>
              }
            </div>

            <button type="submit" disabled={loading} className="wb-btn-primary w-full" data-testid="order-submit">
              {loading ? "Gönderiliyor..." : `Siparişi Tamamla — ${formatTL(subtotal)}`}
            </button>
          </form>

          <aside className="wb-card p-6 h-fit sticky top-28">
            <div className="wb-overline">Sipariş Özeti</div>
            <div className="mt-4 space-y-3 max-h-80 overflow-y-auto">
              {items.map((it) => {
                const unit = isDealer && it.price > 0 && dealerDiscount > 0 ? it.price * (1 - dealerDiscount / 100) : it.price;
                return (
                  <div key={it.product_id} className="flex items-center gap-3 text-sm pb-3 border-b border-white/5 last:border-0">
                    <div className="w-12 h-12 bg-[#0c1f44] flex-shrink-0 overflow-hidden">
                      {it.image_url && <img src={it.image_url} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{it.name}</div>
                      <div className="text-xs text-[#a0aabf]">{it.quantity} × {it.price > 0 ? formatTL(unit) : "—"}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-5 pt-4 border-t border-white/10 space-y-2 text-sm">
              {isDealer && dealerDiscount > 0 && (
                <div className="flex justify-between text-[#d4af37]">
                  <span>Bayi indirim</span><span>%{dealerDiscount}</span>
                </div>
              )}
              <div className="flex justify-between font-serif text-xl">
                <span>Toplam</span>
                <span className="text-[#d4af37]">{formatTL(subtotal)}</span>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[#a0aabf]">KDV %20 dahildir</div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
